import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateShoppingCartItem,
  ShoppingCartItem,
  UpdateShoppingCartItem
} from '@thro-types/leihbar';

import { AxiosRequestConfig } from 'axios';
import { useDispatch } from 'react-redux';

import {
  createShoppingCartItem,
  deleteShoppingCartItem,
  getShoppingCart,
  getShoppingCartAvailability,
  updateShoppingCartItem
} from 'api/leihbar';
import { ShoppingCartActions } from 'app/containers/LeihBar/ShoppingCart';

export const useShoppingCartQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery<ShoppingCartItem[], Error>({
    queryKey: ['shoppingCart', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      return getShoppingCart(config);
    }
  });

  return query;
};

export const useShoppingCartAvailabilityQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['shoppingCart', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const articles = getShoppingCartAvailability(config);
      return articles;
    },
    placeholderData: keepPreviousData
  });

  return query;
};

export const useShoppingCartItemMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const queryKey = ['shoppingCart'];

  const create = useMutation<ShoppingCartItem, Error, CreateShoppingCartItem>({
    mutationFn: async args => {
      return await createShoppingCartItem(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const update = useMutation<ShoppingCartItem, Error, { id: number; data: UpdateShoppingCartItem }>(
    {
      mutationFn: async args => {
        return await updateShoppingCartItem(args.id, args.data, config);
      },
      onSuccess: data => {
        queryClient.invalidateQueries({ queryKey });
      },
      onError: data => {}
    }
  );

  const deletion = useMutation<ShoppingCartItem, Error, number>({
    mutationFn: async args => {
      return await deleteShoppingCartItem(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      dispatch(ShoppingCartActions.setItems([]));
    },
    onError: data => {}
  });

  return { create, update, deletion } as const;
};
